/* eslint-disable react/react-in-jsx-scope */
import { useState, useEffect } from "react"
import PropTypes from "prop-types"

const AudioControls = ({
  audioUrl,
  index,
  isPlaying,
  toggleAudioPlayback,
  stopAudioPlayback,
  audioRefs,
  sourceInfo,
}) => {
  const [currentTime, setCurrentTime] = useState("00:00")
  const [duration, setDuration] = useState("00:00")
  const [progress, setProgress] = useState(0)
  const [collapsed, setCollapsed] = useState(false)

  // Sync audio element state with isPlaying prop
  useEffect(() => {
    const audioElement = audioRefs.current[index]
    if (audioElement) {
      if (isPlaying[index] && audioElement.paused) {
        audioElement.play().catch((error) => {
          console.error(`Error playing audio ${index}:`, error)
        })
      } else if (!isPlaying[index] && !audioElement.paused) {
        audioElement.pause()
      }
    }
  }, [isPlaying, index, audioRefs])

  // Update time and progress
  useEffect(() => {
    const audioElement = audioRefs.current[index]
    if (!audioElement) return

    const updateTime = () => {
      const current = audioElement.currentTime
      const total = audioElement.duration || 0

      setCurrentTime(formatTime(current))
      setDuration(formatTime(total))
      setProgress((current / total) * 100 || 0)
    }

    audioElement.addEventListener("timeupdate", updateTime)
    audioElement.addEventListener("loadedmetadata", updateTime)

    return () => {
      audioElement.removeEventListener("timeupdate", updateTime)
      audioElement.removeEventListener("loadedmetadata", updateTime)
    }
  }, [audioRefs, index])

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return "00:00"
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
  }

  const handleDownload = () => {
    if (!audioUrl) return

    const link = document.createElement("a")
    link.href = audioUrl
    link.download = `audio_response_${index}.mp3`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const toggleCollapse = () => {
    setCollapsed(!collapsed)
  }

  return (
    <div className={`audio-response ${collapsed ? "collapsed" : ""}`}>
      <div className="audio-response-header">
        <div className="audio-response-title">
          <span>Audio Response</span>
        </div>
        <div className="audio-response-actions">
          <button
            onClick={toggleCollapse}
            className="audio-response-action"
            aria-label={collapsed ? "Expand audio player" : "Collapse audio player"}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d={collapsed ? "M12 8l6 6H6l6-6z" : "M12 16l-6-6h12l-6 6z"}
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <button
            onClick={() => stopAudioPlayback(index)}
            className="audio-response-action"
            aria-label="Close audio player"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          <div className="audio-player-container">
            <div className="audio-player-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            <div className="track-info">
              <div className="track-title">Audio Response</div>
              <div className="track-artist">SynapzeLM</div>
            </div>

            <div className="progress-container">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <div className="time-display">
                <span>{currentTime}</span>
                <span>{duration}</span>
              </div>
            </div>

            <div className="audio-controls-container">
              <button className="audio-control-btn">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M19 12L5 21V3L19 12Z" fill="currentColor" transform="rotate(180 12 12)" />
                </svg>
              </button>

              <button className="audio-control-btn play-pause-btn" onClick={() => toggleAudioPlayback(index)}>
                {isPlaying[index] ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" fill="currentColor" />
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M8 5v14l11-7z" fill="currentColor" />
                  </svg>
                )}
              </button>

              <button className="audio-control-btn">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M19 12L5 21V3L19 12Z" fill="currentColor" />
                </svg>
              </button>
            </div>
          </div>

          {sourceInfo && (
            <div className="audio-response-info">
              <span className="source">Source: {sourceInfo}</span>
            </div>
          )}

          <button className="download-btn" onClick={handleDownload}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </>
      )}

      <audio
        ref={(el) => (audioRefs.current[index] = el)}
        src={audioUrl}
        onEnded={() => stopAudioPlayback(index)}
        onError={(e) => console.error(`Audio error for index ${index}:`, e.nativeEvent)}
      />
    </div>
  )
}

AudioControls.propTypes = {
  audioUrl: PropTypes.string,
  index: PropTypes.number.isRequired,
  isPlaying: PropTypes.object.isRequired,
  toggleAudioPlayback: PropTypes.func.isRequired,
  stopAudioPlayback: PropTypes.func.isRequired,
  audioRefs: PropTypes.shape({
    current: PropTypes.array,
  }).isRequired,
  sourceInfo: PropTypes.string,
}

export default AudioControls;

